import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldStarIcon as ShieldStar, TrashIcon as Trash, FloppyDiskIcon as FloppyDisk } from "@phosphor-icons/react";

import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RowListSkeleton } from "@/components/SkeletonList";
import { formatApiError } from "@/lib/utils-app";

/**
 * Admin · Seller Standards
 *
 * Three sub-tools on one page:
 *   1. Version history — view past versions, publish a new one (preserves
 *      history; older attestations keep their version reference).
 *   2. Breed rules — toggle which breeds REQUIRE explicit per-listing
 *      seller-standards acknowledgment (e.g., snub-nosed, working breeds).
 *   3. Verified-Standards attestations — list every breeder badge,
 *      revoke (or un-revoke) with reason for audit.
 */
export default function AdminSellerStandards() {
  const [tab, setTab] = useState("versions");
  return (
    <AppShell>
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#0F8043] font-bold flex items-center gap-1.5 mb-2">
          <ShieldStar size={12} weight="fill" /> Admin · Seller standards
        </p>
        <h1 className="font-spectral text-4xl tracking-tight">Seller standards</h1>
        <p className="text-sm text-[#6B6B6B] mt-2 max-w-2xl">
          Publish new versions of the seller standards, mark specific breeds as
          requiring per-listing acknowledgment, and audit / revoke
          Verified-Standards badges.
        </p>
      </div>

      <div className="border-b border-[#E5E5E5] mb-6 flex gap-2 overflow-x-auto">
        {[
          ["versions", "Version history"],
          ["breeds", "Breed rules"],
          ["attestations", "Attestations"],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            data-testid={`ss-admin-tab-${id}`}
            className={`relative h-10 px-4 text-[13px] font-semibold transition-colors ${
              tab === id ? "text-[#0E0E0E]" : "text-[#6B6B6B] hover:text-[#0E0E0E]"
            }`}
          >
            {label}
            {tab === id && (
              <span className="absolute bottom-0 inset-x-3 h-0.5 bg-[#C9A227]" />
            )}
          </button>
        ))}
      </div>

      {tab === "versions" && <VersionsTab />}
      {tab === "breeds" && <BreedRulesTab />}
      {tab === "attestations" && <AttestationsTab />}
    </AppShell>
  );
}

function VersionsTab() {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({
    version: "",
    effective_date: "",
    version_notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/seller-standards/versions");
      setVersions(r.data?.versions || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const publish = async () => {
    if (!draft.version.trim()) {
      toast.error("Version label required (e.g., v1.1).");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/admin/seller-standards/versions", {
        version: draft.version.trim(),
        effective_date: draft.effective_date.trim() || undefined,
        version_notes: draft.version_notes.trim() || undefined,
      });
      toast.success(`Version ${draft.version.trim()} published`);
      setDraft({ version: "", effective_date: "", version_notes: "" });
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="ss-versions-tab">
      <div className="rounded-2xl bg-[#FBF7EC] border border-[#E8D9A6] p-4 mb-6">
        <p className="text-[12px] text-[#8A6A00] mb-3 leading-relaxed">
          Publishing a new version preserves history. Existing attestations
          keep their old version reference; sellers will need to re-attest
          if you want them on the new version. Inheritance: new version
          inherits sections from the current published version unless you
          provide an override (advanced — use the JSON API if needed).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input
            data-testid="ss-new-version-label"
            placeholder="Version label (e.g., v1.1)"
            value={draft.version}
            onChange={(e) => setDraft({ ...draft, version: e.target.value })}
          />
          <Input
            data-testid="ss-new-version-date"
            placeholder="Effective date (YYYY-MM-DD)"
            value={draft.effective_date}
            onChange={(e) => setDraft({ ...draft, effective_date: e.target.value })}
          />
          <Button
            onClick={publish}
            disabled={submitting}
            data-testid="ss-publish-version"
            className="rounded-full bg-[#0E0E0E] hover:bg-[#0F8043] text-white"
          >
            <FloppyDisk size={14} weight="bold" className="mr-1.5" />
            {submitting ? "Publishing…" : "Publish version"}
          </Button>
        </div>
        <Textarea
          placeholder="What changed (changelog note)"
          value={draft.version_notes}
          onChange={(e) => setDraft({ ...draft, version_notes: e.target.value })}
          className="mt-3 min-h-[60px] text-[13px]"
          data-testid="ss-new-version-notes"
        />
      </div>

      {loading ? (
        <RowListSkeleton />
      ) : (
        <ul className="space-y-2">
          {versions.map((v) => (
            <li
              key={v.version}
              data-testid={`ss-version-${v.version}`}
              className="rounded-xl border border-[#E5E5E5] bg-white px-4 py-3 flex items-center justify-between gap-3"
            >
              <div>
                <p className="font-semibold text-ink">
                  {v.version}
                  {v.is_current && (
                    <span className="ml-2 inline-flex items-center text-[10px] uppercase tracking-[0.14em] font-bold text-[#0F8043] bg-[#0F8043]/10 px-2 py-0.5 rounded-full">
                      Current
                    </span>
                  )}
                </p>
                <p className="text-[12px] text-[#6B6B6B]">
                  Effective {v.effective_date} · published{" "}
                  {v.created_at?.split("T")[0]}
                </p>
                {v.version_notes && (
                  <p className="text-[12px] text-[#6B6B6B] mt-1 italic">
                    {v.version_notes}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function BreedRulesTab() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draftBreed, setDraftBreed] = useState("");
  const [draftRequired, setDraftRequired] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/seller-standards/breed-rules");
      setRules(r.data?.rules || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const upsert = async (breed, required) => {
    try {
      await api.post("/admin/seller-standards/breed-rules", {
        breed,
        required,
      });
      toast.success(`${breed} · ${required ? "required" : "optional"}`);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <section data-testid="ss-breeds-tab">
      <div className="rounded-2xl bg-[#FBF7EC] border border-[#E8D9A6] p-4 mb-6">
        <p className="text-[12px] text-[#8A6A00] mb-3 leading-relaxed">
          Mark a breed as <strong>required</strong> to force the seller to
          tick the per-listing seller-standards acknowledgment before
          publishing. Useful for snub-nosed breeds, working breeds with
          breed-specific genetic panels, or any breed where buyer
          disclosure history shows it's worth the friction.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input
            data-testid="ss-breed-input"
            placeholder="Breed name (e.g., French Bulldog)"
            value={draftBreed}
            onChange={(e) => setDraftBreed(e.target.value)}
          />
          <label className="flex items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              checked={draftRequired}
              onChange={(e) => setDraftRequired(e.target.checked)}
              className="accent-[#0F8043]"
              data-testid="ss-breed-required-check"
            />
            Required at listing
          </label>
          <Button
            onClick={() => {
              if (!draftBreed.trim()) {
                toast.error("Breed name required.");
                return;
              }
              upsert(draftBreed.trim(), draftRequired);
              setDraftBreed("");
            }}
            data-testid="ss-breed-save"
            className="rounded-full bg-[#0E0E0E] hover:bg-[#0F8043] text-white"
          >
            <FloppyDisk size={14} weight="bold" className="mr-1.5" /> Save rule
          </Button>
        </div>
      </div>

      {loading ? (
        <RowListSkeleton />
      ) : rules.length === 0 ? (
        <p className="text-[#6B6B6B] text-sm">
          No breed rules yet — every breed defaults to "encouraged but not required".
        </p>
      ) : (
        <ul className="space-y-2">
          {rules.map((r) => (
            <li
              key={r.breed_lower}
              data-testid={`ss-breed-rule-${r.breed_lower}`}
              className="rounded-xl border border-[#E5E5E5] bg-white px-4 py-3 flex items-center justify-between gap-3"
            >
              <div>
                <p className="font-semibold text-ink">{r.breed}</p>
                <p className="text-[12px] text-[#6B6B6B]">
                  {r.required ? "Required at listing time" : "Optional"} · updated{" "}
                  {r.updated_at?.split("T")[0]}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => upsert(r.breed, !r.required)}
                  data-testid={`ss-breed-toggle-${r.breed_lower}`}
                  className="rounded-full h-8 px-3 text-[12px]"
                >
                  {r.required ? "Make optional" : "Make required"}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AttestationsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/seller-standards/attestations");
      setRows(r.data?.attestations || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const revoke = async (user_id) => {
    const reason = prompt("Reason for revocation (will be stored on the audit record):");
    if (!reason || reason.trim().length < 4) return;
    try {
      await api.post("/admin/seller-standards/revoke", { user_id, reason: reason.trim() });
      toast.success("Badge revoked");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const unrevoke = async (user_id) => {
    if (!confirm("Restore this seller's Verified Standards badge?")) return;
    try {
      await api.post("/admin/seller-standards/unrevoke", { user_id, reason: "admin-restore" });
      toast.success("Badge restored");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) return <RowListSkeleton />;
  if (rows.length === 0) {
    return <p className="text-[#6B6B6B] text-sm" data-testid="ss-attest-empty">No attestations on file yet.</p>;
  }

  return (
    <section data-testid="ss-attestations-tab">
      <ul className="space-y-2">
        {rows.map((a) => (
          <li
            key={a.user_id}
            data-testid={`ss-attestation-${a.user_id}`}
            className={`rounded-xl border bg-white px-4 py-3 flex items-center justify-between gap-3 ${
              a.revoked_at ? "border-[#C1272D]/50" : "border-[#E5E5E5]"
            }`}
          >
            <div className="min-w-0">
              <p className="font-semibold text-ink truncate">
                {a.public_display_name || a.user_email || a.user_id}
                {a.revoked_at && (
                  <span className="ml-2 inline-flex items-center text-[10px] uppercase tracking-[0.14em] font-bold text-crimson bg-[#C1272D]/10 px-2 py-0.5 rounded-full">
                    Revoked
                  </span>
                )}
              </p>
              <p className="text-[12px] text-[#6B6B6B] truncate">
                {a.user_email} · v{a.version} · attested{" "}
                {a.attested_at?.split("T")[0]}
              </p>
              {a.revoke_reason && (
                <p className="text-[11px] text-crimson italic mt-0.5">
                  Reason: {a.revoke_reason}
                </p>
              )}
            </div>
            {a.revoked_at ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => unrevoke(a.user_id)}
                data-testid={`ss-unrevoke-${a.user_id}`}
                className="rounded-full h-8 px-3 text-[12px]"
              >
                Restore badge
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => revoke(a.user_id)}
                data-testid={`ss-revoke-${a.user_id}`}
                className="rounded-full h-8 px-3 text-[12px] border-[#C1272D] text-crimson"
              >
                <Trash size={12} className="mr-1" /> Revoke
              </Button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
